import {Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {SubmitNewDocService} from "../../services/submit-new-doc.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ProtestInfo} from "../../models/protest";
import {SidebarService} from "../../services/sidebar.service";
import {StateService} from "../../services/state.service";
import {MatSelectChange} from "@angular/material/select";
import {CKEditorConfig, DocInfo, InterpolationDetails} from "../../models/template";
import {AttorneyInfoResponse, WholeDocInfo} from "../../models/docket";
import {User, USER_INIT, UserRole, UserRoleString, UserStatus} from "../../models/user";
import {UtilService} from "../../services/util.service";
import {EPDSFile} from "../../models/file-upload";
import {ClassicEditor} from "ckeditor5";
import {catchError, finalize, Subscription, tap, throwError} from "rxjs";
import {TemplateService} from "../../services/template.service";
import {CKEditorComponent} from "@ckeditor/ckeditor5-angular";
import {FileUploadService} from "../../services/file-upload.service";
import {CaseDocketService} from "../../services/case-docket.service";
import {FileUploadComponent} from "../file-upload/file-upload.component";

@Component({
    selector: 'app-submit-new-documents',
    templateUrl: './submit-new-documents.component.html',
    styleUrl: './submit-new-documents.component.scss',
    standalone: false
})
export class SubmitNewDocumentsComponent implements OnInit, OnDestroy {
  @ViewChild('editor', { static: false }) editor!: CKEditorComponent;
  @ViewChild('fileUpload', { static: false }) fileUpload!: FileUploadComponent;

  protected readonly ClassicEditor = ClassicEditor;
  protected readonly CKEditorConfig = CKEditorConfig;

  a_No = '';
  protestInfo!: ProtestInfo;
  doc_InfoList!: WholeDocInfo[];
  docketSub!: Subscription;

  currentDocInfo?: DocInfo;
  docGroups: string[] = [];
  documentsByGroup = new Map<string, DocInfo[]>();
  filteredDocList: WholeDocInfo[] = [];
  selectedDocType?: number;
  docDescFiller?: string;

  originalSelectedOptionDocId!: number;
  isAlwaysVisible!: boolean;
  isAlwaysProtected!: boolean;
  isFileUploadNotRequired!: boolean;
  isProtectedDecisionDocType!: boolean;
  attachAssociatedDoc = true;
  requiredFileType!: string[];
  uploadDocMessage = '';
  fileUploadInProgress = false;

  primaryFiles: EPDSFile[] = [];
  associatedFiles: EPDSFile[] = [];
  attachAssociatedDocOutput?: boolean;
  primaryUploadText = '';
  associatedDocsOnly = false;

  isEditorVisible = false;
  isReviewVisible = false;
  editorContent?: string;
  docFileName?: string;
  saving = false;
  showGenerateDocTempButton = false;

  isDocConfidential?: string;
  comments?: string;

  isAnd = false;
  isGenTemplateValid = false
  isUploadValid = false;
  isFormValid = false;

  attorneyInfoPlaceholder: User = USER_INIT;

  fillerDialogOptions = {
    cancelBtn: true,
    confirmText: "OK",
    cancelText : "Cancel",
    confirmButtonColor: "primary",
    cancelButtonColor:"warn",
    getResponse: true,
    getResponseMax: 250
  }

  constructor(private readonly util: UtilService,
              private readonly activatedRoute: ActivatedRoute,
              private readonly router: Router,
              private readonly sidebarService: SidebarService,
              private readonly stateService: StateService,
              private readonly submitNewDocService: SubmitNewDocService,
              private readonly templateService: TemplateService,
              private readonly fileUploadService: FileUploadService,
              private caseDocketService: CaseDocketService) {
  }

  ngOnInit() {
    this.a_No = this.activatedRoute.snapshot.params['a_No'];
    this.caseDocketService.loadDocket(this.a_No);
    this.docketSub = this.caseDocketService.docketSub$.subscribe((response: AttorneyInfoResponse|undefined) => {
      if (!response) return;
      this.protestInfo = response.protestInfo;
      this.getSubmitNewDocForm();
      this.sidebarService.setNavByUser(this.stateService.user.role_id, response.protestInfo);
    });
  }

  ngOnDestroy(): void {
    if (this.a_No) {
      this.docketSub.unsubscribe();
    }
  }

  getSubmitNewDocForm() {
    this.submitNewDocService.getSubmitNewDocForm(this.a_No).subscribe(resp => {
      console.debug(resp);
      this.doc_InfoList = resp.doc_InfoList;

      this.filterDocTypeList();
      this.groupByRole();
      this.sortDocTypeList();
      this.configureUploadFileTypes();
      this.checkValidations();
    });
  }

  filterDocTypeList() {
    const caseType = this.protestInfo.case_Type.toUpperCase();

    if ((caseType.indexOf('PRO') > -1) || (caseType === 'PROTEST')) {
      this.filteredDocList = this.doc_InfoList.filter(docInfo => docInfo.case_Type === 'PROTEST');
    } else if ((caseType.indexOf('RECON') > -1) || (caseType === 'RECONSIDERATION')) {
      this.filteredDocList = this.doc_InfoList.filter(docInfo => docInfo.case_Type === 'RECONSIDERATION');
    } else if ((caseType.indexOf('ENT') > -1) || caseType === 'ENTITLEMENT') {
      this.filteredDocList = this.doc_InfoList.filter(docInfo => docInfo.case_Type === 'ENTITLEMENT');
    } else if ((caseType.indexOf('COST') > -1) || caseType === 'COST-CLAIM') {
      this.filteredDocList = this.doc_InfoList.filter(docInfo => docInfo.case_Type === 'COSTS');
    }

    if (this.stateService.userRole != UserRole.GAO_Admin) {
      this.filteredDocList = this.filteredDocList.filter(docInfo => {
          return ![1, 163, 164, 165].includes(docInfo.doc_Type_Id);
      });
    }

    // sort entire list by filing order before grouping
    this.filteredDocList.sort((a, b) => {
        if (a.filing_Order === b.filing_Order) {
          return 0;
        } else if (a.filing_Order === null) {
          return 1;
        } else if (b.filing_Order === null) {
          return -1;
        } else {
          return  a.filing_Order - b.filing_Order;
        }
      });
  }

  sortDocTypeList() {
    // Sort items by filing order withing the group
    this.documentsByGroup.forEach((list, key) => {
      const sortedListByFiling = list.sort((a, b) => {
        if (a.filing_Order === b.filing_Order) {
          return 0;
        } else if (a.filing_Order === null) {
          return 1;
        } else if (b.filing_Order === null) {
          return -1;
        } else {
          return  a.filing_Order - b.filing_Order;
        }
      });

      this.documentsByGroup.set(key, sortedListByFiling);
    });

  }

  groupByRole() {
    this.filteredDocList.forEach(item => {
      const group = this.documentsByGroup.get(item.role) ?? [];
      group.push(item);
      this.documentsByGroup.set(item.role, group);
    });

    this.docGroups = Array.from(this.documentsByGroup.keys());
  }

  configureUploadFileTypes() {
    if (this.stateService.userRole === UserRole.Protester || this.stateService.userRole === UserRole.Intervenor) {
      this.uploadDocMessage = this.fileUploadService.udmPdfExcel;
      this.requiredFileType = this.fileUploadService.rftPdfExcel;
    } else if (this.stateService.userRole === UserRole.Agency_Admin || this.stateService.userRole === UserRole.Agency_Attorney) {
      this.uploadDocMessage = this.fileUploadService.udmPdfWordExcelZip;
      this.requiredFileType = this.fileUploadService.rftPdfWordExcelZip;
    } else if (this.stateService.userRole === UserRole.GAO_Attorney || this.stateService.userRole === UserRole.GAO_Supervisor) {
      this.uploadDocMessage = this.fileUploadService.udmPdfWordExcel;
      this.requiredFileType = this.fileUploadService.rftPdfWordExcel;
    } else if (this.stateService.userRole === UserRole.GAO_Admin) {
      this.uploadDocMessage = this.fileUploadService.udmPdfWordExcelZip;
      this.requiredFileType = this.fileUploadService.rftPdfWordExcelZip;
    } else {
      this.uploadDocMessage = '';
    }
  }

  onDocTypeChange(event: MatSelectChange) {
    console.debug(event.value);

    this.currentDocInfo = event.value;
    this.selectedDocType = this.currentDocInfo?.doc_Type_Id;
    this.docDescFiller = undefined;
    this.docFileName = undefined;
    this.isEditorVisible = false;
    this.isReviewVisible = false;
    this.associatedDocsOnly = false;

    if (this.selectedDocType != undefined) {
      console.debug(this.selectedDocType);
      this.editDocumentDescriptions();
    }

    if (!this.showGenerateDocTempButton) {
      this.isEditorVisible = false;
      this.isReviewVisible = false;
      this.docFileName = undefined;
    }

    this.checkValidations();
  }

  editDocumentDescriptions() {
    if (this.currentDocInfo) {
      const listOfTemplateDocIDs = [103, 104, 105, 106, 107, 109, 110, 112, 114, 115, 116, 117, 119, 120, 122, 124, 125, 126, 127, 128, 129, 131, 133, 134, 135, 136, 137, 138, 140, 160, 161, 191, 193, 195, 197, 217, 218, 219, 220, 235, 236, 237, 238];
      this.showGenerateDocTempButton = listOfTemplateDocIDs.includes(this.currentDocInfo.doc_Type_Id);

      const listOfAlwaysVisibleDocIds = [2, 3, 4, 5, 12, 17, 19, 20, 21, 22, 27, 28, 30, 31, 32, 33, 38, 39, 41, 42, 43, 44, 46, 53, 54, 56, 57, 58, 59, 67, 70, 82, 83, 91, 92, 100, 101, 103, 104, 105, 106, 107, 108, 111, 112, 114, 115, 116, 117, 118, 121, 122, 124, 125, 126, 127, 130, 131, 133, 134, 135, 136, 139, 140, 142, 143, 144, 145, 147, 154, 155, 157, 158, 159, 160, 161, 162, 172, 180, 235, 236, 237, 238]
      this.isAlwaysVisible = listOfAlwaysVisibleDocIds.includes(this.currentDocInfo.doc_Type_Id);

      const listOfUploadRequiredDocIds = [27, 38, 12, 179, 53, 100, 91, 82, 67, 154, 234, 233, 231, 232, 215, 214, 213, 212, 211, 210]
      this.isFileUploadNotRequired = listOfUploadRequiredDocIds.includes(this.currentDocInfo.doc_Type_Id);

      const listOfAlwaysProtectedDocIds = [120, 110, 138, 129, 55, 84, 93, 102, 71, 156, 40, 168, 29, 18, 220, 219, 218, 217];
      this.isAlwaysProtected = listOfAlwaysProtectedDocIds.includes(this.currentDocInfo.doc_Type_Id);

      const listOfAndedDocIDs = [110, 120, 138, 129];
      this.isProtectedDecisionDocType = listOfAndedDocIDs.includes(this.currentDocInfo.doc_Type_Id);

      if (this.currentDocInfo.doc_Type_Desc.indexOf("_") > -1) {
        this.util.displayDialogMessage(this.currentDocInfo.doc_Type_Desc, "Please briefly describe the document " + "you are filing (e.g., objection to agency's 5 day letter, request for an extension).",
          this.fillerDialogOptions)
          .subscribe(res => {
            console.debug(res);
            this.processFillerResponse(res);
          });
      }

      // if this is notice of appearance or request to intervene there should be  no associated documents
      if (this.currentDocInfo.doc_Type_Id === 160 || this.currentDocInfo.doc_Type_Id === 161 || this.currentDocInfo.doc_Type_Id === 0) {
        this.attachAssociatedDoc = false;
      }

      this.primaryUploadText = this.showGenerateDocTempButton ? 'Upload Template' : 'Upload Primary Document';

    }

  }

  processFillerResponse(res: any) {
    if (res.response != undefined && this.currentDocInfo) {
      this.docDescFiller = this.util.removeCurlyQuotes(res.response.replace(/\n/g, ''));
      if (!this.originalSelectedOptionDocId) this.originalSelectedOptionDocId = this.currentDocInfo.doc_Type_Id;
      let tempDoc_Type_Desc = this.currentDocInfo.doc_Type_Desc.slice(0, this.currentDocInfo.doc_Type_Desc.indexOf("_"))
      tempDoc_Type_Desc = tempDoc_Type_Desc.concat(this.docDescFiller!);

      const tempDocInfo: WholeDocInfo = {
        case_Type: this.currentDocInfo.case_Type,
        doc_Type_Desc: tempDoc_Type_Desc,
        doc_Type_Id: 0,
        role: this.currentDocInfo.role,
        filing_Order: this.currentDocInfo.filing_Order
      }

      console.debug(tempDocInfo);

      this.documentsByGroup.forEach(docGroup => {
        docGroup.forEach(aDoc => {
          if (aDoc.doc_Type_Id === 0) {
            const docIndex = docGroup.indexOf(aDoc);
            docGroup.splice(docIndex, 1);
          }

        });
      });

      this.documentsByGroup.get(this.currentDocInfo.role)?.push(tempDocInfo);
      this.currentDocInfo = tempDocInfo;
      this.selectedDocType = tempDocInfo.doc_Type_Id;
    } else {
      this.currentDocInfo = undefined;
      this.selectedDocType = undefined;
    }
  }

  onEPDSFileListChange(epdsFileList: EPDSFile[], typeOfList: string): void {
    console.debug("EPDS File list for type %s: %O", typeOfList, epdsFileList);

    if (typeOfList === 'P') {
      this.primaryFiles = epdsFileList;
    } else {
      this.associatedFiles = epdsFileList;
    }

    console.debug(this.primaryFiles);
    console.debug(this.associatedFiles);
    this.checkValidations();
  }

  onAttachAssociatedDocOutput(attach: boolean) {
    this.attachAssociatedDocOutput = attach;
    this.checkValidations();
  }

  onDocConfidentialChange() {
    this.checkValidations();
  }

  onEditorSave() {
    this.editorContent = this.editorContent = this.editor.editorInstance?.getData()
    const selectedDocType = (this.selectedDocType === 0) ? this.originalSelectedOptionDocId : this.selectedDocType;

    if (this.editorContent && (selectedDocType != undefined)) {
      this.saving = true;

      this.templateService.createPdf(this.editorContent, selectedDocType)
        .pipe(finalize(() => this.saving = false))
        .subscribe(doc => {
          this.docFileName = ((this.selectedDocType === 0) ? this.util.toCamelCase(this.currentDocInfo?.doc_Type_Desc!) : doc.fileName) + '.pdf';
          this.isEditorVisible = false;
          this.isReviewVisible = true;
          this.associatedDocsOnly = !this.isAnd && this.showGenerateDocTempButton && (this.docFileName != null)

          this.checkValidations();
        });
    } else {
      console.error(this.editorContent);
      console.error(selectedDocType);
      this.util.displayDialogMessage("Error", "There was a problem saving the template updates.");
    }
  }

  onEditorCancel() {
    this.isEditorVisible = false;
    this.checkValidations();
  }

  onViewDocument() {
    const filename = this.docFileName;
    const selectedDocType = (this.selectedDocType === 0) ? this.originalSelectedOptionDocId : this.selectedDocType;

    if (filename && (selectedDocType != undefined)) {
      this.templateService.downloadTempPdf(filename, selectedDocType);
    } else {
      console.error(selectedDocType);
      console.error(filename);
      this.util.displayDialogMessage("Error", "There was a problem generating the temporary PDF.");
    }
  }

  onClearEdits() {
    this.isReviewVisible = false;
    this.docFileName = undefined;
    this.associatedDocsOnly = false;
    this.checkValidations();
  }

  onEditTemplate() {
    this.isEditorVisible = true;
  }

  onGenerateTemplate() {
    const selectedDocType = (this.selectedDocType === 0) ? this.originalSelectedOptionDocId : this.selectedDocType;

    if (selectedDocType) {
      this.templateService.generateTemplateBasedOnDocumentType(selectedDocType, this.a_No).subscribe(templateDetails => {
        console.debug(templateDetails);
        this.editorContent = templateDetails.htmlContent;

        // todaysDate used in templates
        const todaysDate = new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });

        const reportDueDate = templateDetails.reportDueDate;
        const attorneyInfo = templateDetails.attorneyInfo ? { ...templateDetails.attorneyInfo } : this.attorneyInfoPlaceholder;
        // @ts-ignore
        const templateInput: InterpolationDetails = { protestInfo: { ...templateDetails.protestInfo }, attorneyInfo: { ...attorneyInfo }, todaysDate, reportDueDate };
        const protestInfoObj = templateInput.protestInfo;
        const attorneyInfoObj = templateInput.attorneyInfo;

        if (protestInfoObj) {
          Object.keys(protestInfoObj).forEach(key => {
            // @ts-ignore
            protestInfoObj[key] = protestInfoObj[key] == null ? '' : protestInfoObj[key];
          });
        }

        if (attorneyInfoObj) {
          Object.keys(attorneyInfoObj).forEach(key => {
            // @ts-ignore
            attorneyInfoObj[key] = attorneyInfoObj[key] == null ? '' : attorneyInfoObj[key];
          });
        }

        this.interpolateUsingEvalWIthJavascriptTemplateLiterals(templateInput);

        this.isEditorVisible = true;
      });
    }
  }

  interpolateUsingEvalWIthJavascriptTemplateLiterals(interpolationDetails: InterpolationDetails) {
    if (this.editorContent) {
      // Convert angular template interpolation {{ }} to java template literals ${ } so we can do interpolation in javascript
      this.editorContent = this.editorContent.replace(/{{/g, '${').replace(/}}/g, '}');

      this.editorContent = this.renderTemplate(this.editorContent, interpolationDetails);
    }
  }

  onSubmit() {
    this.saving = true;

    if ((this.primaryFiles.length > 0) || (this.associatedFiles.length > 0)) {
      if (this.associatedDocsOnly) {
        this.primaryFiles = [];
      }

      const attachmentType = this.util.toCamelCase(this.currentDocInfo?.doc_Type_Desc!);
      const allFiles = [
        ...this.primaryFiles.map(epdsFile => ( { epdsFile, attachmentType })),
        ...(this.associatedFiles.map?.length ? this.associatedFiles.map(epdsFile => ( { epdsFile, attachmentType })) : [])
      ];

      console.debug(allFiles);
      this.fileUploadInProgress = true;

      this.fileUploadService.submitFile(allFiles, this.protestInfo.a_No, this.fileUpload)
        .pipe(finalize(() => this.fileUploadInProgress = false))
        .pipe(tap(resp => console.log('Response from submitFile:', resp)),
        catchError(err => {
          // console.error('Error in submitFile:', err);
          this.saving = false;  // submitDocInfo handled success case
          return throwError(err);
        })
      ).subscribe(resp => {
        console.debug(resp);
        this.submitDocInfo();
      });
    } else {
      this.submitDocInfo();
    }

  }

  submitDocInfo() {
    const requestType = 'other';
    const selectedDocType = (this.selectedDocType === 0) ? this.originalSelectedOptionDocId : this.selectedDocType;

    this.submitNewDocService.submitDocInfo(requestType, this.editorContent, selectedDocType, this.protestInfo.a_No,
                                           this.currentDocInfo?.doc_Type_Desc, this.isDocConfidential, this.comments, this.docDescFiller)
    .subscribe(resp => {
      console.log(resp);
      this.saving = false;
      if (resp) {
        if (resp.inputErrors) {
          let message = "";
          let getResponse = false;
          resp.inputErrors.forEach(inputError => {
            if (inputError.fieldName == "typeofdocument") {
              getResponse = true;
              message += "The type of document has invalid characters, please enter the corrected value below and re-submit (Your files successfully uploaded)";
            } else {
              message += inputError.invalidValue + ": " + inputError.message + '<br>';
            }
          });
          if (getResponse) {
            this.util.displayDialogMessage("Input Errors", message, {...this.fillerDialogOptions, response: this.docDescFiller})
              .subscribe(res => {
                this.currentDocInfo!.doc_Type_Desc = this.currentDocInfo!.doc_Type_Desc.slice(0, this.currentDocInfo!.doc_Type_Desc.indexOf(this.docDescFiller!)) + "_";
                if (res) this.processFillerResponse(res);
              });
          } else {
            this.util.displayDialogMessage("Input Errors", message);
          }
        } else {
          this.caseDocketService.loadDocket(this.a_No, true);
          this.router.navigate([UserRoleString[this.stateService.user.role_id] + '/case-docketsheet/' + this.a_No]);
        }
      }
    });
  }

  resetFields() {
    this.editorContent = undefined;
    this.isEditorVisible = false;
    this.isReviewVisible = false;
    this.docFileName = undefined;
    this.comments = undefined;
    this.primaryUploadText = '';
    this.primaryFiles = [];
    this.associatedFiles = [];
    this.associatedDocsOnly = false;
    this.isDocConfidential = undefined;
  }

  onReset() {
    this.currentDocInfo = undefined;
    this.selectedDocType = undefined;
    this.resetFields();
    this.checkValidations();
  }

  renderTemplate(templateStr: string, args: any): string {
    templateStr = templateStr.replace(/`/g, '\\`');

    const keys = Object.keys(args);
    const fn = new Function(...keys, 'return `' + templateStr + '`');

    return fn(...keys.map(key => args[key]));
  }

  checkValidations() {
    this.setIsAnd();
    this.setIsGenTemplateValid();
    this.setIsUploadValid();
    this.setIsFormValid();
  }

  setIsAnd() {
    this.isAnd = this.showGenerateDocTempButton && this.isProtectedDecisionDocType;
  }

  setIsGenTemplateValid() {
    this.isGenTemplateValid = this.showGenerateDocTempButton && this.isReviewVisible && (this.docFileName != undefined);
  }

  setIsUploadValid() {
    if (this.isFileUploadNotRequired) {
      this.isUploadValid = true;
    } else {
      if (this.attachAssociatedDocOutput != undefined) {
        if (this.attachAssociatedDocOutput) {

          if (this.associatedDocsOnly && (this.associatedFiles.length > 0)) {
            this.isUploadValid = true;
          } else {
            this.isUploadValid = (this.primaryFiles.length > 0) &&  (this.associatedFiles.length > 0)
          }

        } else {
          this.isUploadValid = this.associatedDocsOnly ||  (!this.associatedDocsOnly && (this.primaryFiles.length > 0))
        }
      } else {
        this.isUploadValid = false;
      }

      if (!this.isAlwaysVisible && !this.isFileUploadNotRequired && !this.isAlwaysProtected) {
        this.isUploadValid = this.isUploadValid && this.isDocConfidential != undefined;
      }
    }
  }

  setIsFormValid() {
    this.isFormValid = this.isAnd ? (this.isGenTemplateValid && this.isUploadValid) : ((this.isGenTemplateValid && (this.isUploadValid)) || this.isUploadValid);
  }

}
